import type { GameDifficulty } from "../common/types";
import {
  COLS,
  HIDDEN_ROWS,
  KICKS_I,
  KICKS_JLSTZ,
  LINE_SCORES,
  LOCK_DELAY_MS,
  MAX_LOCK_RESETS,
  PIECES,
  ROWS,
  SHAPES,
  SOFT_DROP_MS,
  TSPIN_SCORES,
} from "./constants";
import type {
  ActivePiece,
  ClearResult,
  EngineEvent,
  EngineListener,
  EngineSnapshot,
  EngineStatus,
  LastAction,
  PieceId,
  TSpinKind,
} from "./types";

function emptyBoard(): (PieceId | null)[][] {
  return Array.from({ length: ROWS }, () =>
    Array.from({ length: COLS }, () => null)
  );
}

function shuffleBag(): PieceId[] {
  const bag = [...PIECES];
  for (let i = bag.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const current = bag[i];
    const swap = bag[j];
    if (current === undefined || swap === undefined) continue;
    bag[i] = swap;
    bag[j] = current;
  }
  return bag;
}

function spawnX(id: PieceId): number {
  return id === "O" ? 4 : 3;
}

function matrixOf(id: PieceId, rotation: number): number[][] {
  const rotations = SHAPES[id];
  return rotations[rotation % 4] ?? rotations[0] ?? [];
}

function pieceCells(
  piece: ActivePiece
): Array<{ x: number; y: number }> {
  const matrix = matrixOf(piece.id, piece.rotation);
  const cells: Array<{ x: number; y: number }> = [];
  for (let row = 0; row < matrix.length; row += 1) {
    const line = matrix[row];
    if (!line) continue;
    for (let col = 0; col < line.length; col += 1) {
      if (line[col]) {
        cells.push({ x: piece.x + col, y: piece.y + row });
      }
    }
  }
  return cells;
}

function gravityMs(difficulty: GameDifficulty, level: number): number {
  const base =
    difficulty === "easy" ? 880 : difficulty === "hard" ? 380 : 620;
  const curve =
    difficulty === "easy" ? 0.07 : difficulty === "hard" ? 0.18 : 0.12;
  return Math.max(70, base / (1 + (level - 1) * curve));
}

export class TetrisEngine {
  private board = emptyBoard();
  private active: ActivePiece | null = null;
  private hold: PieceId | null = null;
  private canHold = true;
  private bag: PieceId[] = [];
  private queue: PieceId[] = [];
  private score = 0;
  private lines = 0;
  private combo = -1;
  private highScore = 0;
  private status: EngineStatus = "playing";
  private difficulty: GameDifficulty;
  private lastAction: LastAction = "spawn";
  private lastKickUsed = false;
  private lastClear: ClearResult | null = null;
  private backToBack = false;
  private dropAcc = 0;
  private lockAcc = 0;
  private lockResets = 0;
  private listeners = new Set<EngineListener>();

  constructor(difficulty: GameDifficulty = "medium", highScore = 0) {
    this.difficulty = difficulty;
    this.highScore = highScore;
    this.refillQueue();
    this.spawn();
  }

  on(listener: EngineListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): EngineSnapshot {
    return {
      board: this.board.map((row) => [...row]),
      active: this.active ? { ...this.active } : null,
      ghostY: this.ghostY(),
      hold: this.hold,
      canHold: this.canHold,
      queue: this.queue.slice(0, 3),
      score: this.score,
      lines: this.lines,
      level: this.level,
      combo: Math.max(0, this.combo),
      highScore: this.highScore,
      status: this.status,
      lastClear: this.lastClear,
      difficulty: this.difficulty,
    };
  }

  get level(): number {
    return 1 + Math.floor(this.lines / 10);
  }

  setDifficulty(difficulty: GameDifficulty): void {
    this.difficulty = difficulty;
  }

  togglePause(): void {
    if (this.status === "over") return;
    this.status = this.status === "paused" ? "playing" : "paused";
  }

  resume(): void {
    if (this.status === "paused") this.status = "playing";
  }

  pause(): void {
    if (this.status === "playing") this.status = "paused";
  }

  tick(dt: number, softDropping = false): void {
    if (this.status !== "playing" || !this.active) return;

    const interval = softDropping
      ? SOFT_DROP_MS
      : gravityMs(this.difficulty, this.level);

    this.dropAcc += dt;
    if (this.dropAcc >= interval) {
      this.dropAcc = 0;
      const moved = this.tryMove(0, 1);
      if (moved && softDropping) {
        this.score += 1;
        this.emit({ type: "soft-drop", dropDistance: 1 });
      }
      if (!moved) {
        this.lockAcc += interval;
      } else {
        this.lockAcc = 0;
        this.lastAction = "drop";
      }
    }

    if (this.isGrounded()) {
      this.lockAcc += dt;
      if (this.lockAcc >= LOCK_DELAY_MS) {
        this.lockPiece();
      }
    } else {
      this.lockAcc = 0;
      this.lockResets = 0;
    }
  }

  move(dx: number): boolean {
    if (this.status !== "playing") return false;
    const moved = this.tryMove(dx, 0);
    if (moved) {
      this.lastAction = "move";
      this.resetLockIfGrounded();
      this.emit({ type: "move" });
    }
    return moved;
  }

  rotate(dir: 1 | -1): boolean {
    if (this.status !== "playing" || !this.active) return false;
    const from = this.active.rotation;
    const to = (from + dir + 4) % 4;
    const kicks =
      this.active.id === "O"
        ? ([[0, 0]] as Array<[number, number]>)
        : this.active.id === "I"
          ? (KICKS_I[`${from}>${to}`] ?? [[0, 0]])
          : (KICKS_JLSTZ[`${from}>${to}`] ?? [[0, 0]]);

    for (let i = 0; i < kicks.length; i += 1) {
      const kick = kicks[i];
      if (!kick) continue;
      const [kx, kyUp] = kick;
      const next: ActivePiece = {
        ...this.active,
        rotation: to,
        x: this.active.x + kx,
        y: this.active.y - kyUp,
      };
      if (!this.collides(next)) {
        this.active = next;
        this.lastAction = "rotate";
        this.lastKickUsed = i > 0;
        this.resetLockIfGrounded();
        this.emit({ type: "rotate", piece: next.id });
        return true;
      }
    }
    return false;
  }

  hardDrop(): void {
    if (this.status !== "playing" || !this.active) return;
    let distance = 0;
    while (this.tryMove(0, 1)) {
      distance += 1;
    }
    this.score += distance * 2;
    this.lastAction = "drop";
    this.emit({
      type: "hard-drop",
      piece: this.active.id,
      dropDistance: distance,
      cells: pieceCells(this.active),
    });
    this.lockPiece();
  }

  holdPiece(): void {
    if (this.status !== "playing" || !this.active || !this.canHold) return;
    const current = this.active.id;
    const incoming = this.hold;
    this.hold = current;
    this.canHold = false;
    this.active = null;
    this.emit({ type: "hold", piece: current });
    if (incoming) {
      this.spawn(incoming);
    } else {
      this.spawn();
    }
  }

  private get levelValue(): number {
    return this.level;
  }

  private emit(event: EngineEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  private refillQueue(): void {
    while (this.queue.length < 7) {
      if (this.bag.length === 0) this.bag = shuffleBag();
      const next = this.bag.shift();
      if (next) this.queue.push(next);
    }
  }

  private spawn(forced?: PieceId): boolean {
    this.refillQueue();
    const id = forced ?? this.queue.shift();
    this.refillQueue();
    if (!id) return false;

    const piece: ActivePiece = {
      id,
      rotation: 0,
      x: spawnX(id),
      y: 0,
    };

    if (this.collides(piece)) {
      this.active = piece;
      this.status = "over";
      this.emit({ type: "game-over" });
      return false;
    }

    this.active = piece;
    this.dropAcc = 0;
    this.lockAcc = 0;
    this.lockResets = 0;
    this.lastAction = "spawn";
    this.lastKickUsed = false;
    return true;
  }

  private tryMove(dx: number, dy: number): boolean {
    if (!this.active) return false;
    const next = { ...this.active, x: this.active.x + dx, y: this.active.y + dy };
    if (this.collides(next)) return false;
    this.active = next;
    return true;
  }

  private collides(piece: ActivePiece): boolean {
    return pieceCells(piece).some(({ x, y }) => {
      if (x < 0 || x >= COLS || y >= ROWS || y < 0) return true;
      const row = this.board[y];
      return Boolean(row && row[x]);
    });
  }

  private isGrounded(): boolean {
    if (!this.active) return false;
    return this.collides({ ...this.active, y: this.active.y + 1 });
  }

  private resetLockIfGrounded(): void {
    if (!this.isGrounded()) return;
    if (this.lockResets >= MAX_LOCK_RESETS) return;
    this.lockAcc = 0;
    this.lockResets += 1;
  }

  private ghostY(): number {
    if (!this.active) return 0;
    const ghost = { ...this.active };
    while (!this.collides({ ...ghost, y: ghost.y + 1 })) {
      ghost.y += 1;
    }
    return ghost.y;
  }

  private lockPiece(): void {
    if (!this.active) return;
    const cells = pieceCells(this.active);
    const pieceId = this.active.id;
    cells.forEach(({ x, y }) => {
      const row = this.board[y];
      if (row && x >= 0 && x < COLS && y >= 0 && y < ROWS) {
        row[x] = pieceId;
      }
    });

    this.emit({ type: "lock", piece: pieceId, cells });

    const tSpin = this.detectTSpin();
    this.clearLines(tSpin);
    this.active = null;
    this.canHold = true;

    if (this.hasBlockInHidden()) {
      this.status = "over";
      this.emit({ type: "game-over" });
      return;
    }

    this.spawn();
  }

  private hasBlockInHidden(): boolean {
    for (let y = 0; y < HIDDEN_ROWS; y += 1) {
      const row = this.board[y];
      if (row?.some(Boolean)) return true;
    }
    return false;
  }

  private detectTSpin(): TSpinKind {
    if (!this.active || this.active.id !== "T" || this.lastAction !== "rotate") {
      return "none";
    }

    const corners = [
      { x: this.active.x, y: this.active.y },
      { x: this.active.x + 2, y: this.active.y },
      { x: this.active.x, y: this.active.y + 2 },
      { x: this.active.x + 2, y: this.active.y + 2 },
    ];

    const filled = corners.filter(({ x, y }) => {
      if (x < 0 || x >= COLS || y < 0 || y >= ROWS) return true;
      return Boolean(this.board[y]?.[x]);
    }).length;

    if (filled >= 3) return "full";
    if (filled === 2 && this.lastKickUsed) return "mini";
    return "none";
  }

  private clearLines(tSpin: TSpinKind): ClearResult | null {
    const fullRows: number[] = [];
    for (let y = 0; y < ROWS; y += 1) {
      const row = this.board[y];
      if (row && row.every(Boolean)) fullRows.push(y);
    }

    if (fullRows.length === 0 && tSpin === "none") {
      this.combo = -1;
      this.lastClear = null;
      return null;
    }

    if (fullRows.length > 0) {
      this.board = this.board.filter((_, index) => !fullRows.includes(index));
      while (this.board.length < ROWS) {
        this.board.unshift(Array.from({ length: COLS }, () => null));
      }
    }

    this.combo = this.combo < 0 ? 0 : this.combo + 1;
    const lineCount = fullRows.length;
    const isTetris = lineCount === 4;
    const difficult = isTetris || tSpin !== "none";
    const applyB2B = difficult && this.backToBack;
    this.backToBack = difficult ? true : lineCount > 0 ? false : this.backToBack;

    const baseTable = tSpin === "none" ? LINE_SCORES : TSPIN_SCORES[tSpin];
    let gain = (baseTable[lineCount] ?? 0) * this.levelValue;
    if (applyB2B) gain = Math.floor(gain * 1.5);
    if (this.combo > 0) gain += 50 * this.combo * this.levelValue;

    this.score += gain;
    this.lines += lineCount;
    this.highScore = Math.max(this.highScore, this.score);

    const result: ClearResult = {
      lines: lineCount,
      tSpin,
      isTetris,
      combo: Math.max(0, this.combo),
      scoreGain: gain,
      backToBack: applyB2B,
      rows: fullRows,
    };
    this.lastClear = result;

    if (lineCount > 0 || tSpin !== "none") {
      this.emit({ type: "clear", clear: result, piece: this.active?.id });
    }
    if (isTetris) this.emit({ type: "tetris", clear: result });
    if (tSpin !== "none") this.emit({ type: "tspin", clear: result });

    return result;
  }
}
